"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { DropAuthorBadges } from "./DropAuthorBadges";
import { useCallback } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useCompactMode } from "@/contexts/CompactModeContext";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode | undefined;
  readonly showActionsButton?: boolean | undefined;
  readonly onOpenActions?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(54);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge,
    showActionsButton: t1,
    onOpenActions
  } = t0;
  const showActionsButton = t1 === undefined ? false : t1;
  const router = useRouter();
  const compact = useCompactMode();
  let t2;
  if ($[0] !== router) {
    t2 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleNavigation = t2;
  const t3 = `tw-mb-0 tw-font-semibold tw-leading-none ${compact ? "tw-text-sm" : "tw-text-md"}`;
  const t4 = drop.author.handle ?? drop.author.id;
  let t5;
  if ($[2] !== drop.author.handle || $[3] !== drop.author.primary_address || $[4] !== handleNavigation) {
    t5 = e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`);
    $[2] = drop.author.handle;
    $[3] = drop.author.primary_address;
    $[4] = handleNavigation;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `/${drop.author.handle ?? drop.author.primary_address}`;
  let t7;
  if ($[6] !== drop.author.handle || $[7] !== t5 || $[8] !== t6) {
    t7 = <Link onClick={t5} href={t6} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{drop.author.handle}</Link>;
    $[6] = drop.author.handle;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t7) {
    t8 = <UserProfileTooltipWrapper user={t4}>{t7}</UserProfileTooltipWrapper>;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t8) {
    t9 = <p className={t3}>{t8}</p>;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== drop.author.level) {
    t10 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[16] = drop.author.level;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = `header-author-badges-${drop.id}`;
  let t12;
  if ($[18] !== drop.author || $[19] !== t11) {
    t12 = <DropAuthorBadges profile={drop.author} tooltipIdPrefix={t11} />;
    $[18] = drop.author;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== drop.created_at) {
    t14 = <WaveDropTime timestamp={drop.created_at} />;
    $[22] = drop.created_at;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t12 || $[26] !== t14 || $[27] !== t9) {
    t15 = <div className="tw-flex tw-items-center tw-gap-x-2">{t9}{t10}{t12}{t13}{t14}</div>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t14;
    $[27] = t9;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== badge) {
    t16 = !!badge && <div className="tw-ml-2">{badge}</div>;
    $[29] = badge;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t15 || $[32] !== t16) {
    t17 = <div className="tw-flex tw-items-center tw-gap-x-2">{t15}{t16}</div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== onOpenActions || $[35] !== showActionsButton) {
    t18 = showActionsButton && onOpenActions && <button type="button" aria-label="Open drop actions" onClick={onOpenActions} className="tw-hidden tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-1.5 tw-text-iron-300 active:tw-bg-iron-800 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-iron-100 md:tw-flex"><EllipsisVerticalIcon className="tw-h-5 tw-w-5" /></button>;
    $[34] = onOpenActions;
    $[35] = showActionsButton;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t17 || $[38] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2">{t17}{t18}</div>;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== drop.wave || $[41] !== handleNavigation || $[42] !== showWaveInfo) {
    t20 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-leading-0 -tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[40] = drop.wave;
    $[41] = handleNavigation;
    $[42] = showWaveInfo;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <div>{t20}</div>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== currentPartIndex || $[47] !== isStorm || $[48] !== partsCount) {
    t22 = isStorm && <div className="tw-relative tw-mt-2 tw-inline-flex"><span className="tw-mb-1.5 tw-text-xs tw-text-iron-50">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[46] = currentPartIndex;
    $[47] = isStorm;
    $[48] = partsCount;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t19 || $[51] !== t21 || $[52] !== t22) {
    t23 = <>{t19}{t21}{t22}</>;
    $[50] = t19;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
};
export default WaveDropHeader;